import React from 'react';
import { NOPHOTO_IMAGE } from '../Assets/StaticData/StaticImage';
import { formatDate } from '../controllers/FormatAmount';
import { clientData as globalClientData } from '../controllers/GetClient';

export const isImageUrl = (url, fieldName = '') => {
  if (typeof url !== 'string' || !url.trim()) return false;
  const str = url.trim().toLowerCase();

  // Data URIs
  if (str.startsWith('data:image/')) return true;

  // Direct image extension check (ignoring query parameters / hash)
  const cleanUrl = str.split('?')[0].split('#')[0];
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.svg', '.gif', '.bmp', '.avif', '.ico', '.tiff'];
  if (imageExtensions.some((ext) => cleanUrl.endsWith(ext))) return true;

  // Known image CDNs / upload paths
  const knownImagePaths = [
    'cdn.shopea',
    'cloudinary.com',
    's3.amazonaws.com',
    'storage.googleapis.com',
    '/uploads/',
    '/custom-fields/',
    '/image/upload/',
  ];
  if (knownImagePaths.some((p) => str.includes(p))) return true;

  // Field name hints when value is an http/https URL or path
  const fieldLower = String(fieldName || '').toLowerCase();
  const imageFieldKeywords = ['image', 'img', 'photo', 'avatar', 'picture', 'banner', 'logo', 'icon', 'thumbnail', 'cover'];
  const hasImageKeyword = imageFieldKeywords.some((kw) => fieldLower.includes(kw));

  if (hasImageKeyword && (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('/uploads/'))) {
    return true;
  }

  return false;
};

export const isColorValue = (val, fieldName = '') => {
  if (typeof val !== 'string' || !val.trim()) return false;
  const str = val.trim();

  // Hex format (#fff, #ffffff, #ffffffff)
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(str)) return true;

  // RGB, RGBA, HSL, HSLA
  if (/^(rgb|rgba|hsl|hsla)\([^)]+\)$/i.test(str)) return true;

  // If field name explicitly indicates color and value is a valid standard CSS color
  const fieldLower = String(fieldName || '').toLowerCase();
  if ((fieldLower.includes('color') || fieldLower.includes('colour')) && /^[a-zA-Z]+$/.test(str) && str.length < 20) {
    return true;
  }

  return false;
};

export const RenderCustomFieldValue = ({ val, fieldName = '', clientData = null }) => {
  const activeClientData = clientData || globalClientData;

  if (val === null || val === undefined || val === '') {
    return <span className='text-gray-400 dark:text-gray-500'>-</span>;
  }

  // 1. Boolean
  if (typeof val === 'boolean') {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${val
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
          }`}
      >
        {val ? 'Yes' : 'No'}
      </span>
    );
  }

  // 2. Arrays
  if (Array.isArray(val)) {
    if (val.length === 0) return <span className='text-gray-400 dark:text-gray-500'>-</span>;

    // Check if array elements are images
    const imageItems = val.filter((item) => typeof item === 'string' && isImageUrl(item, fieldName));
    if (imageItems.length > 0) {
      return (
        <div className='flex items-center gap-1 my-0.5 overflow-hidden'>
          {imageItems.slice(0, 3).map((imgUrl, idx) => (
            <img
              key={idx}
              src={imgUrl}
              alt={`${fieldName} ${idx + 1}`}
              className='w-7 h-7 object-cover rounded border border-gray-200 dark:border-gray-700 shadow-xs shrink-0'
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = NOPHOTO_IMAGE;
              }}
            />
          ))}
          {imageItems.length > 3 && (
            <span className='text-[11px] font-medium text-gray-500 dark:text-gray-400 pl-1'>
              +{imageItems.length - 3}
            </span>
          )}
        </div>
      );
    }

    return (
      <span className='truncate block' title={val.join(', ')}>
        {val.length} item{val.length !== 1 ? 's' : ''}
      </span>
    );
  }

  // 3. Image URL (single string)
  if (typeof val === 'string' && isImageUrl(val, fieldName)) {
    return (
      <div className='flex items-center py-0.5'>
        <img
          src={val}
          alt={fieldName || 'image'}
          className='w-8 h-8 object-cover rounded border border-gray-200 dark:border-gray-700 shadow-xs shrink-0'
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = NOPHOTO_IMAGE;
          }}
        />
      </div>
    );
  }

  // 4. Color hex / rgb
  if (isColorValue(val, fieldName)) {
    return (
      <div className='flex items-center gap-1.5 font-mono text-xs'>
        <span
          className='w-3.5 h-3.5 rounded-full border border-gray-300 dark:border-gray-600 shadow-xs shrink-0 inline-block'
          style={{ backgroundColor: val }}
          title={val}
        />
        <span className='uppercase truncate'>{val}</span>
      </div>
    );
  }

  // 5. Date / Time / ISO Date string
  const keyLower = String(fieldName || '').toLowerCase();
  const isDateKey =
    keyLower.includes('createdat') ||
    keyLower.includes('updatedat') ||
    keyLower.includes('created_at') ||
    keyLower.includes('updated_at') ||
    (keyLower.endsWith('date') && !keyLower.includes('candidate'));
  const isIsoDateString =
    typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val);

  if (isDateKey || isIsoDateString) {
    const formattedDate = formatDate(val, activeClientData);
    return <span className='truncate block' title={formattedDate}>{formattedDate}</span>;
  }

  // Helper to format address objects into readable string
  const formatAddress = (addr) => {
    if (!addr || typeof addr !== 'object') return null;
    const parts = [
      addr.addressLine1 || addr.address_line_1 || addr.address1 || addr.address || addr.street,
      addr.addressLine2 || addr.address_line_2 || addr.address2,
      addr.city || addr.district,
      addr.state || addr.province,
      addr.pincode || addr.pin_code || addr.zip || addr.zipcode || addr.postal_code,
      addr.country,
    ]
      .filter((p) => p && typeof p === 'string' && p.trim().length > 0)
      .map((p) => p.trim());

    if (parts.length > 0) {
      return parts.join(', ');
    }
    return null;
  };

  // Check if string is a JSON encoded address or object
  if (typeof val === 'string' && val.trim().startsWith('{') && val.trim().endsWith('}')) {
    try {
      const parsed = JSON.parse(val);
      if (parsed && typeof parsed === 'object') {
        const formattedAddr = formatAddress(parsed);
        if (formattedAddr) {
          return <span className='truncate block' title={formattedAddr}>{formattedAddr}</span>;
        }
      }
    } catch (e) {
      // Not valid JSON, continue with normal flow
    }
  }

  // 6. Object
  if (typeof val === 'object' && val !== null) {
    // Address object formatting
    const formattedAddr = formatAddress(val);
    if (formattedAddr) {
      return <span className='truncate block' title={formattedAddr}>{formattedAddr}</span>;
    }

    if (val?.shop_name) {
      return <span className='truncate block font-medium' title={val.shop_name}>{val.shop_name}</span>;
    }
    if (val?.name) {
      return <span className='truncate block font-medium' title={val.name}>{val.name}</span>;
    }
    if (val?.title) {
      return <span className='truncate block font-medium' title={val.title}>{val.title}</span>;
    }
    if (val?.email) {
      return <span className='truncate block' title={val.email}>{val.email}</span>;
    }
    const str = JSON.stringify(val);
    return <span className='truncate block text-xs' title={str}>{str}</span>;
  }

  // 7. General Web Link (non-image URL)
  if (typeof val === 'string' && (val.startsWith('http://') || val.startsWith('https://'))) {
    return (
      <a
        href={val}
        target='_blank'
        rel='noopener noreferrer'
        onClick={(e) => e.stopPropagation()}
        className='text-indigo-600 dark:text-indigo-400 hover:underline truncate block text-xs'
        title={val}
      >
        {val}
      </a>
    );
  }

  // 8. Text with HTML tags (strip tags)
  if (typeof val === 'string' && /<[^>]+>/.test(val)) {
    const cleanText = val.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return <span className='truncate block' title={cleanText}>{cleanText || '-'}</span>;
  }

  // 9. Standard String / Number / Default
  return <span className='truncate block' title={String(val)}>{String(val)}</span>;
};

export default RenderCustomFieldValue;
